import type { BlogPost } from '@/hooks/useBlogPosts';

// Posts used to live only in localStorage, which caps out around 5MB. A single
// uploaded cover image (stored as a base64 data URL) can eat most of that, so
// saves silently failed once the quota was hit. Posts are now kept in
// IndexedDB, which has plenty of room for text plus embedded images.
// localStorage is still used as a fast first-paint cache, but it is best-effort
// only and never allowed to break a save.

export const BLOG_CACHE_KEY = 'blog_posts_v3';

const DB_NAME = 'federica_blog_db';
const DB_VERSION = 1;
const STORE_NAME = 'blog';
const POSTS_KEY = 'posts';

function getFactory(): IDBFactory | null {
  try {
    if (typeof indexedDB === 'undefined' || !indexedDB) return null;
    return indexedDB;
  } catch {
    return null;
  }
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const factory = getFactory();
    if (!factory) {
      reject(new Error('IndexedDB is not available in this browser'));
      return;
    }
    const request = factory.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open the blog database'));
  });
}

function isPostList(value: unknown): value is BlogPost[] {
  return Array.isArray(value) && value.every((item) => item && typeof item === 'object' && 'id' in item);
}

/** Read the full post list from IndexedDB. Returns null when nothing is stored yet. */
export async function readPostsFromDb(): Promise<BlogPost[] | null> {
  const db = await openDatabase();
  try {
    const value = await new Promise<unknown>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(POSTS_KEY);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Could not read blog posts'));
    });
    return isPostList(value) && value.length > 0 ? value : null;
  } finally {
    db.close();
  }
}

/** Persist the full post list to IndexedDB. Throws if the write fails. */
export async function writePostsToDb(posts: BlogPost[]): Promise<void> {
  const db = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(posts, POSTS_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Could not save blog posts'));
      tx.onabort = () => reject(tx.error ?? new Error('The blog save was cancelled by the browser'));
    });
  } finally {
    db.close();
  }
}

/** Quick synchronous read of the local cache, used to render instantly on load. */
export function readCachedPosts(): BlogPost[] {
  try {
    const raw = localStorage.getItem(BLOG_CACHE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return isPostList(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Best-effort cache write. Never throws — IndexedDB is the source of truth. */
export function writeCachedPosts(posts: BlogPost[]): void {
  try {
    localStorage.setItem(BLOG_CACHE_KEY, JSON.stringify(posts));
  } catch {
    // Quota exceeded or storage disabled: the IndexedDB copy is still intact.
  }
}